import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import EditIcon from "@material-ui/icons/Edit";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import LockIcon from "@material-ui/icons/Lock";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import RoleModal from "../../components/RoleModal";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

import { listRoles, deleteRole , duplicateRole } from "../../services/access.api";

/** Textos de la pantalla. Varios llevan interpolación, de ahí el segundo argumento. */
const t = (clave, vars) => i18n.t(`roleMatrix.page.${clave}`, vars);

/**
 * El ámbito llega crudo de la base ('empresa', 'equipo', 'personal'): sin esto
 * el panel en inglés mostraba la palabra en español.
 */
const traducirAmbito = (scope) =>
  i18n.t(`roleMatrix.scopes.${scope}`, { defaultValue: scope });

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    marginTop: -8,
    marginBottom: 8
  },
  scopeChip: {
    padding: "2px 10px",
    borderRadius: 12,
    fontSize: 12,
    background: theme.palette.action.selected,
    textTransform: "capitalize"
  },
  code: { fontFamily: "monospace", fontWeight: 500 },
  systemIcon: { fontSize: 15, verticalAlign: "middle", marginLeft: 6, opacity: 0.5 }
}));

/**
 * Roles y permisos.
 *
 * Muestra los roles del sistema junto a los propios de la empresa: el
 * administrador debe VER `SUPERADMIN`, `ADMIN`, `SUPERVISOR` y `USER` aunque no
 * pueda editarlos (doc 0380 D15) — es lo que refleja la maqueta del cliente.
 */
const Roles = () => {
  const classes = useStyles();

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listRoles();
      setRoles(data ?? []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  // Duplicar: los roles de sistema son inmutables a proposito —desmarcar
  // `roles:editar` de ADMIN dejaria a la empresa sin poder editar roles, ni
  // siquiera para deshacerlo—. La copia da libertad sin quitar la red.
  const [dupSource, setDupSource] = useState(null);
  const [dupCode, setDupCode] = useState("");
  const [dupName, setDupName] = useState("");
  const [dupSaving, setDupSaving] = useState(false);

  const openDuplicate = (role) => {
    setDupSource(role);
    setDupCode(`${role.code}${t("copyCodeSuffix")}`);
    setDupName(`${role.name}${t("copyNameSuffix")}`);
  };

  const closeDuplicate = () => {
    setDupSource(null);
    setDupCode("");
    setDupName("");
  };

  const handleDuplicate = async () => {
    setDupSaving(true);
    try {
      await duplicateRole(dupSource.id, { code: dupCode, name: dupName });
      toast.success(t("duplicated"));
      closeDuplicate();
      fetchRoles();
    } catch (err) {
      toastError(err);
    } finally {
      setDupSaving(false);
    }
  };

  const handleEdit = (role) => {
    setSelectedRoleId(role.id);
    setModalOpen(true);
  };

  const handleNew = () => {
    setSelectedRoleId(null);
    setModalOpen(true);
  };

  const handleDelete = async () => {
    try {
      await deleteRole(deleting.id);
      toast.success(t("deleted"));
      fetchRoles();
    } catch (err) {
      // El backend responde 409 con el número de usuarios que aún lo tienen:
      // ese mensaje es más útil que un genérico, así que se muestra tal cual.
      toastError(err);
    } finally {
      setDeleting(null);
      setConfirmOpen(false);
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deleting ? t("confirmDeleteTitle", { name: deleting.name }) : ""}
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={handleDelete}
      >
        {t("confirmDeleteBody")}
      </ConfirmationModal>

      <RoleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        roleId={selectedRoleId}
        onSaved={fetchRoles}
      />

      <MainHeader>
        <Title>{t("title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleNew}
          >
            {t("newRole")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.subtitle}>
          {t("subtitle")}
        </div>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("colCode")}</TableCell>
              <TableCell>{t("colName")}</TableCell>
              <TableCell align="center">{t("colScope")}</TableCell>
              <TableCell align="center">{t("colPerms")}</TableCell>
              <TableCell align="center">{t("colUsers")}</TableCell>
              <TableCell align="center">{t("colActions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRowSkeleton columns={6} />
            ) : (
              roles.map((role) => (
                <TableRow key={role.id} hover>
                  <TableCell className={classes.code}>
                    {role.code}
                    {role.isSystem && (
                      <Tooltip title={t("systemTooltip")}>
                        <LockIcon className={classes.systemIcon} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>{role.name}</TableCell>
                  <TableCell align="center">
                    <span className={classes.scopeChip}>{traducirAmbito(role.scope)}</span>
                  </TableCell>
                  <TableCell align="center">{role.permissionsCount}</TableCell>
                  <TableCell align="center">{role.usersCount}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleEdit(role)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      title={t("duplicate")}
                      onClick={() => openDuplicate(role)}
                    >
                      <FileCopyOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={role.isSystem}
                      onClick={() => {
                        setDeleting(role);
                        setConfirmOpen(true);
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
      <Dialog open={Boolean(dupSource)} onClose={closeDuplicate} maxWidth="xs" fullWidth>
        <DialogTitle>
          {t("duplicateTitle", { name: dupSource?.name || "" })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText style={{ fontSize: 13 }}>
            {t("duplicateHint", { total: dupSource?.permissionsCount || 0 })}
          </DialogContentText>
          <TextField
            label={t("colCode")}
            value={dupCode}
            onChange={(e) => setDupCode(e.target.value.toUpperCase())}
            fullWidth
            margin="dense"
            autoFocus
          />
          <TextField
            label={t("colName")}
            value={dupName}
            onChange={(e) => setDupName(e.target.value)}
            fullWidth
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDuplicate} disabled={dupSaving}>
            {i18n.t("roleMatrix.modal.cancel")}
          </Button>
          <Button
            onClick={handleDuplicate}
            color="primary"
            variant="contained"
            disabled={dupSaving || !dupCode.trim() || !dupName.trim()}
          >
            {t("duplicate")}
          </Button>
        </DialogActions>
      </Dialog>

    </MainContainer>
  );
};

export default Roles;
